import styled from 'styled-components'
import { Transition } from 'utils/constants'

export const SelectWrap = styled.div`
  width: 100%;
  position: relative;

  &.has-error {
    .Select__control {
      border-color: var(--t-input-has-error);
      background-color: var(--t-input-has-error);
    }
  }

  &:not(:last-child) {
    margin-bottom: 28px;
  }

  .errorIcon,
  .successIcon {
    position: absolute;
    top: 50%;
    right: 16px;
    transform: translateY(-50%);
    pointer-events: none;
  }
`

export const SelectLabel = styled.div`
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  line-height: 15px;
  color: var(--t-text-label);

  span {
    color: var(--t-forms-label-span-text);
  }
`

export const StyledOption = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`

export const OptionText = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 90%;
`
export const SelectError = styled.div`
  color: var(--t-input-text-error);
  font-size: 11px;
  font-weight: 500;
  line-height: 14px;
  margin-top: 5px;
`

export const HorizontalValueContainerWrap = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  flex-wrap: nowrap;

  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  .Select__multi-value {
    flex: 0 0 auto;
  }

  .Select__input-container {
    flex: 0 0 auto;
  }
`

export const ScrollableValueContainerWrap = styled.div`
  width: 100%;
  min-width: 0;

  .Select__multi-value {
    margin: 0 !important;
  }

  .Select__input-container {
    margin: 0 !important;
    padding: 1px 0px;
  }

  .Select__multi-value__label {
    padding: 1px 0 2px 8px;
  }

  .simplebar-content:before {
    content: none;
  }

  .simplebar-content:after {
    content: none;
  }

  .simplebar-content {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    padding: 2px 0;
  }

  .simplebar-vertical {
    width: 4px !important;
    right: -46px;
  }

  .simplebar-horizontal {
    height: 0px;
    display: none;
  }

  .Select__placeholder {
    ${({ $menuIsOpen }) => $menuIsOpen && 'display: none;'}
  }
`

export const createCustomStyles = (scrollableContent) => ({
  option: (styles, state) => {
    return {
      ...styles,
      cursor: 'pointer',
      backgroundColor: state.isFocused
        ? 'var(--t-select-option-hover-bg)'
        : 'transparent',
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: '17px',
      paddingLeft: '16px',
      color: 'var(--t-text-input)',
      outline: 'none !important',

      '&:active': {
        backgroundColor: 'var(--t-select-option-active-bg)',
      },
    }
  },
  control: (styles, state) => ({
    ...styles,
    backgroundColor: 'var(--t-surface-2)',
    transition: `all ${Transition.ms300}`,
    minHeight: '42px',
    cursor: 'pointer',
    outline: 'none !important',
    boxShadow: 'none !important',
    borderColor: state.isFocused
      ? 'var(--t-action-accent)'
      : 'var(--t-surface-2)',
    borderRadius: '8px',
    ...(scrollableContent ? { height: 'auto' } : { height: '42px' }),

    '&:hover': {
      borderColor: state.isFocused
        ? 'var(--t-action-accent)'
        : 'var(--t-input-hover)',
    },
  }),
  singleValue: (styles) => ({
    ...styles,
    color: 'var(--t-text-input)',
    fontWeight: 500,
  }),
  placeholder: (styles) => ({
    ...styles,
    color: 'var(--t-text-secondary)',
    fontWeight: 400,
  }),
  indicatorSeparator: (styles) => ({
    ...styles,
    display: 'none',
  }),
  clearIndicator: (styles) => ({
    ...styles,
    display: 'none',
  }),
  valueContainer: (styles) => ({
    ...styles,
    fontWeight: 500,
    fontSize: '14px',
    maxWidth: '100%',
    lineHeight: '17px',
    color: 'var(--p-neutral-500)',
    paddingLeft: '16px',

    ...(scrollableContent
      ? {
          height: 'auto !important',
          maxHeight: '120px',
          padding: '0px 50px 0px 14px',
          margin: '10px 0',
          overflow: 'visible',
          flexWrap: 'wrap',
        }
      : {
          flexWrap: 'nowrap',
          overflow: 'hidden',
        }),
  }),

  menu: (styles) => ({
    ...styles,
    zIndex: 15,
    marginTop: '8px',
    boxShadow:
      '0px 2px 2px -1px rgba(27, 35, 44, 0.04), 0px 8px 16px -2px rgba(27, 36, 44, 0.12)',
    border: `1px solid var(--t-select-popover-border)`,
    borderRadius: '6px',
    backgroundColor: 'var(--t-select-popover-bg)',
  }),
  multiValue: (styles) => ({
    ...styles,
    backgroundColor: 'var(--t-select-multivalue-bg)',
  }),
  multiValueLabel: (styles) => ({
    ...styles,
    color: 'var(--t-select-multivalue-text)',
  }),
  input: (styles) => ({
    ...styles,
    color: 'var(--t-text-input)',
  }),
  multiValueRemove: (styles) => ({
    ...styles,
    transition: '0.3s ease-out',
    '&:hover': {
      backgroundColor: 'var(--t-select-with-checkboxes-button-close-hover-bg)',
      color: 'var(--t-select-with-checkboxes-button-hover-color)',
    },
  }),
  ...(scrollableContent
    ? {
        dropdownIndicator: (styles, props) => {
          return {
            ...styles,
            position: 'absolute',
            right: '12px',
            top: '0',
            ...(props?.selectProps?.menuIsOpen
              ? { transform: 'rotate(180deg)' }
              : {}),
          }
        },
      }
    : {}),
})
